import type { Problem } from '@/lib/api/problems';
import { DEFAULT_STARTER_CODE } from '@/stores/editorStore';
import { generateStub } from './stub';

// resolveStarter returns the starter code for a problem + language, mirroring how
// the workspace picks initial content: an explicit per-language starter_code, else
// a generated function stub (function-mode problems), else the language's default
// stdio template.
export function resolveStarter(problem: Problem, language: string): string {
  let starter = problem.starter_code?.[language];
  if (!starter && problem.execution_mode === 'function' && problem.signature) {
    starter = generateStub(problem.signature, language) || undefined;
  }
  return starter ?? DEFAULT_STARTER_CODE[language] ?? '';
}

// Compare templates with all whitespace stripped. This makes detection robust to
// indentation/whitespace drift between the persisted copy and the current template
// (e.g. an older auto-persisted stdio template, or one re-indented by the editor) —
// which an exact-match check misses, leaving stale boilerplate masking the stub.
// Real solutions won't collapse to a template's whitespace-free form, so they're safe.
const stripWs = (s: string) => s.replace(/\s+/g, '');
const DEFAULT_TEMPLATES = new Set(Object.values(DEFAULT_STARTER_CODE).map(stripWs));

// isAutoStarter reports whether saved code is empty or just an unedited default
// stdio template that was auto-persisted on a previous visit. Such code is safe to
// regenerate — needed when a problem is converted stdio->function after a user
// already opened it, so the stale boilerplate no longer masks the function stub.
// Real user-written solutions won't match a template, so they're kept.
export function isAutoStarter(code: string): boolean {
  if (code.trim() === '') return true;
  return DEFAULT_TEMPLATES.has(stripWs(code));
}
